<script setup lang="ts">
import { $dfs, $unwrapNode } from "@lexical/utils";
import { $getRoot, type SerializedEditorState } from "lexical";
import moment from "moment";
import { computed, nextTick, ref } from "vue";

import Avatar from "~/components/dumb/Avatar.vue";
import Button from "~/components/dumb/Button.vue";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import EmptyState from "~/components/dumb/EmptyState.vue";
import LoadingSpinner from "~/components/dumb/LoadingSpinner.vue";
import Modal from "~/components/dumb/Modal.vue";
import ScrollbarOverlay from "~/components/dumb/ScrollbarOverlay.vue";
import TextInput from "~/components/dumb/TextInput.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { $isRecommendationButtonsNode } from "~/components/text/nodes/RecommendationButtonsNode";
import { $isRecommendationLoadingNode } from "~/components/text/nodes/RecommendationLoadingNode";
import { $isRecommendationWrapperNode } from "~/components/text/nodes/RecommendationWrapperNode";
import SnapshotEditor from "~/components/text/SnapshotEditor.vue";
import { getCopyItems } from "~/components/text/copyItems";
import { getHeadlessEditorWithNodes, loadHeadlessEditorState } from "~/components/text/utils";
import buildInlineDiffState from "~/components/text/utils/textDiff";
import { DEACTIVATED_USER_LABEL } from "~/components/visualization/constants";
import { DotsHorizontalIcon, HistoryFieldIcon, RenameIcon, RestoreIcon } from "~/icons";
import { ButtonSize, ButtonStyle, DropdownMenuItemKind, ModalWidth, Placement } from "~/shared/enums";
import type { DropdownMenuSection, TextSnapshot, TextSnapshotSource } from "~/shared/types";
import { useDataStore } from "~/stores";
import { getFullDateShort } from "~/utils/time";
import usePaginatedList from "~/utils/usePaginatedList";

const props = defineProps<{
  source: TextSnapshotSource;
  viewOnly?: boolean;
}>();

const emit = defineEmits<{
  close: [];
  restore: [state: SerializedEditorState];
}>();

const dataStore = useDataStore();

const selectedIndex = ref(0);
const isRestoring = ref(false);
const scrollbarOverlay = ref<InstanceType<typeof ScrollbarOverlay> | null>(null);
const titleInputRefs = ref<Map<string, InstanceType<typeof TextInput>>>(new Map());

const getDateLabel = (snapshot: TextSnapshot) => getFullDateShort(moment(snapshot.createdAt));

const sourceCreatedAt = (snapshot: TextSnapshot | undefined) =>
  snapshot?.sourceSnapshotDuid
    ? (dataStore.getTextSnapshotByDuidIfLoaded(snapshot.sourceSnapshotDuid)?.createdAt ?? null)
    : null;

const snapshots = computed(() => dataStore.getLoadedTextSnapshotsForSourceOrdered(props.source));

const isRestoreDisabled = (snapshot: TextSnapshot, index: number) =>
  index === 0 || snapshot.createdAt === sourceCreatedAt(snapshots.value[0]);

const { isLoading } = usePaginatedList(
  computed(() => `textSnapshots-${props.source.entityName}-${props.source.entityDuid}`),
  computed(() => scrollbarOverlay.value?.scrollElement ?? null),
  computed(() => snapshots.value.length),
  async () => dataStore.loadPaginatedTextSnapshots(props.source)
);

const selectedSnapshot = computed(() => snapshots.value[selectedIndex.value] ?? null);
const previousSnapshot = computed(() => snapshots.value[selectedIndex.value + 1] ?? null);

const selectedTitle = computed(() => {
  const snapshot = selectedSnapshot.value;
  if (!snapshot) {
    return "";
  }
  return snapshot.name || `Version from ${getDateLabel(snapshot)}`;
});

const getActor = (duid: string | null) => (duid ? dataStore.getUserByDuid(duid) : undefined);
const getActorName = (duid: string | null) => getActor(duid)?.name || DEACTIVATED_USER_LABEL;

const stripRecommendationsFromLexicalState = (state: SerializedEditorState): SerializedEditorState => {
  const editor = getHeadlessEditorWithNodes();
  loadHeadlessEditorState(editor, state);
  editor.update(
    () => {
      $dfs($getRoot()).forEach(({ node }) => {
        if ($isRecommendationButtonsNode(node) || $isRecommendationLoadingNode(node)) {
          node.remove();
        } else if ($isRecommendationWrapperNode(node)) {
          $unwrapNode(node);
        }
      });
    },
    { discrete: true }
  );
  return editor.getEditorState().toJSON();
};

const diffState = computed<SerializedEditorState | null>(() => {
  const selected = selectedSnapshot.value;
  if (!selected) {
    return null;
  }
  const laterState = stripRecommendationsFromLexicalState(selected.text);
  const previous = previousSnapshot.value;
  if (!previous) {
    return laterState;
  }
  return buildInlineDiffState(stripRecommendationsFromLexicalState(previous.text), laterState);
});

const restoreSnapshot = async (snapshot: TextSnapshot) => {
  if (isRestoring.value) {
    return;
  }
  isRestoring.value = true;
  try {
    await dataStore.createTextSnapshotFromRestore(snapshot);
  } catch {
    isRestoring.value = false;
    return;
  }
  emit("restore", snapshot.text);
  emit("close");
};

const selectedRestoreDisabled = computed(() => {
  const snapshot = selectedSnapshot.value;
  return isRestoring.value || !snapshot || isRestoreDisabled(snapshot, selectedIndex.value);
});

const restoreSelected = () => {
  if (selectedSnapshot.value) {
    restoreSnapshot(selectedSnapshot.value);
  }
};

const setTitleInputRef = (duid: string, el: InstanceType<typeof TextInput> | null) => {
  if (!el) {
    titleInputRefs.value.delete(duid);
    return;
  }
  titleInputRefs.value.set(duid, el);
};

const rename = async (snapshot: TextSnapshot) => {
  await nextTick();
  titleInputRefs.value.get(snapshot.duid)?.startEditing(undefined, { persistFocus: true });
};

const onSave = (snapshot: TextSnapshot, name: string) => {
  const trimmed = name.trim();
  if (trimmed === snapshot.name || (!snapshot.name && trimmed === getDateLabel(snapshot))) {
    return;
  }
  dataStore.updateTextSnapshotName(snapshot.duid, trimmed);
};

const sections = (snapshot: TextSnapshot, index: number): DropdownMenuSection[] => [
  {
    title: "Version options",
    items: [
      {
        title: "Restore",
        kind: DropdownMenuItemKind.BUTTON,
        icon: RestoreIcon,
        hidden: props.viewOnly,
        disabled: isRestoreDisabled(snapshot, index),
        onClick: () => restoreSnapshot(snapshot),
      },
      {
        title: "Rename",
        kind: DropdownMenuItemKind.BUTTON,
        icon: RenameIcon,
        hidden: props.viewOnly,
        onClick: () => rename(snapshot),
      },
      ...getCopyItems("Version", stripRecommendationsFromLexicalState(snapshot.text)),
    ],
  },
];
</script>

<template>
  <Modal
    :entity="source"
    title="Version history"
    hide-title
    :width="ModalWidth.XXL"
    :custom-height-pixels="560"
    custom-styles="!p-0 flex h-full overflow-hidden"
    mobile-no-swipe
    @close="emit('close')">
    <LoadingSpinner v-if="isLoading && snapshots.length === 0" class="m-auto" />
    <EmptyState
      v-else-if="snapshots.length === 0"
      title="No version history"
      description="Snapshots will appear here once you start editing, allowing you to review and restore previous versions"
      :main-icon="HistoryFieldIcon"
      hide-button />
    <div v-else class="flex h-full">
      <div class="flex w-full min-w-0 flex-col">
        <div v-if="selectedSnapshot" class="flex items-center gap-4 bg-std px-[26px] py-4">
          <span class="flex-1 truncate text-lg font-semibold text-md select-none">{{ selectedTitle }}</span>
        </div>
        <div class="flex flex-1 overflow-y-auto px-5 pb-6">
          <SnapshotEditor v-if="diffState" :key="selectedSnapshot?.duid" :initial-state="diffState" />
        </div>
      </div>
      <div class="flex h-full w-64 shrink-0 flex-col border-l border-lt bg-std">
        <span class="pt-4 pr-5 pb-1.5 pl-4 text-xs font-semibold text-vlt uppercase select-none">Version history</span>
        <ScrollbarOverlay ref="scrollbarOverlay" class="h-full">
          <div class="flex h-full flex-col gap-1 px-2.5 pt-2 pb-6">
            <div
              v-for="(snapshot, index) in snapshots"
              :key="snapshot.duid"
              class="group/entry flex w-full flex-col gap-1 rounded-sm px-2.5 py-1.5 text-left text-sm font-medium whitespace-nowrap text-lt focus-ring-lt outline-hidden"
              :class="index === selectedIndex ? 'bg-lt hover:bg-md' : 'hover:bg-lt'"
              @click="selectedIndex = index"
              @keydown.enter.self="selectedIndex = index">
              <div class="flex items-start gap-1">
                <div class="-ml-0.5 min-w-0 flex-1 overflow-x-hidden">
                  <TextInput
                    :ref="(el) => setTitleInputRef(snapshot.duid, el as InstanceType<typeof TextInput> | null)"
                    :text="snapshot.name || getDateLabel(snapshot)"
                    label="Version name"
                    :editable="!viewOnly"
                    placeholder="Title"
                    dblclick-to-edit
                    class="font-medium text-md"
                    @save="onSave(snapshot, $event)" />
                </div>
                <DropdownMenu
                  :sections="sections(snapshot, index)"
                  :placement="Placement.BOTTOM_RIGHT"
                  :width-pixels="192"
                  :distance="2">
                  <Tooltip text="Options">
                    <button
                      type="button"
                      class="-mt-0.5 -mr-1 flex items-center rounded-sm p-0.5 text-lt opacity-0 focus-ring-std group-hover/entry:opacity-100 hover:bg-hvy"
                      :class="{ '!opacity-100': index === selectedIndex }"
                      @click.stop>
                      <span class="sr-only">Version options</span>
                      <DotsHorizontalIcon class="icon-sm" />
                    </button>
                  </Tooltip>
                </DropdownMenu>
              </div>
              <span v-if="snapshot.name" class="text-xs font-normal text-lt">{{ getDateLabel(snapshot) }}</span>
              <span v-if="sourceCreatedAt(snapshot)" class="text-xs font-normal text-lt">
                Restored from {{ getFullDateShort(moment(sourceCreatedAt(snapshot))) }}
              </span>
              <span v-if="index === 0" class="text-xs font-normal text-lt">Current version</span>
              <span class="flex w-full items-center gap-1 text-xs font-normal text-vlt">
                <Avatar :user="getActor(snapshot.actorDuid)" class="size-4 shrink-0" />
                <span class="truncate">{{ getActorName(snapshot.actorDuid) }}</span>
              </span>
            </div>
            <LoadingSpinner v-if="isLoading" class="mt-1 self-center py-2" />
          </div>
        </ScrollbarOverlay>
        <div v-if="!viewOnly" class="m-2.5">
          <Button
            text="Restore"
            :btn-style="ButtonStyle.PRIMARY"
            :size="ButtonSize.SMALL"
            :disabled="selectedRestoreDisabled"
            class="w-full"
            @click="restoreSelected" />
        </div>
      </div>
    </div>
  </Modal>
</template>
